import React, { useState, useEffect, useRef, useContext } from 'react';
import { Search, AccessTime } from '@material-ui/icons';
import { Box } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useOnClickOutside } from 'src/context/hooks/useOnClickOutside';
import { Context } from 'src/context';
import { useListSearchVehicles } from 'src/pages/searchVehiclesPage/hooks/useListSearchVehicles';
import { history } from 'src/App';

export type FilterType = {
  label: string;
  value: string;
};

type RecentSearch = {
  text: string;
  filters: FilterType[];
};

const filters: FilterType[] = [
  { label: 'CNPJ', value: 'cnpj' },
  { label: 'Chassi', value: 'chassi' },
  { label: 'Ordem de Serviço', value: 'os' },
];

const RECENT_SEARCHES_KEY = 'vehicle-recent-searches';

function VehicleSearchBar() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<FilterType[]>(filters);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { userAuth } = useContext(Context);
  const {
    setSearchFilter,
    setTypeFilter,
    setShouldLoad
  } = useListSearchVehicles();

  useEffect(() => {
    const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setRecentSearches(parsed.slice(0, 3));
        }
      } catch (error) {
        console.error('Error parsing recent searches:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (recentSearches.length > 0) {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.slice(0, 3)));
    }
  }, [recentSearches]);

  const filtersEqual = (a: FilterType[], b: FilterType[]) => {
    if (a.length !== b.length) return false;
    const aVals = a.map(f => f.value).sort();
    const bVals = b.map(f => f.value).sort();
    return aVals.every((val, idx) => val === bVals[idx]);
  };

  const setDefaultAfterSearch = () => {
    setSearchQuery('');
    setIsExpanded(false)
    setSelectedFilter(filters);
    inputRef.current?.blur();
  }

  const onSearch = (search: string, type: string | null) => {
    setSearchFilter(search);
    setTypeFilter(type);
    setShouldLoad(true);
    history.push(userAuth?.admin ? `/admin/search-vehicles` : `/search-vehicles`);
    setDefaultAfterSearch();
  }

  const handleSearch = () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      const exists = recentSearches.some(
        (item) => item.text === trimmedQuery && filtersEqual(item.filters, selectedFilter)
      );
      if (!exists) {
        const newRecent: RecentSearch = {
          text: trimmedQuery,
          filters: selectedFilter,
        };
        const newRecentSearches = [newRecent, ...recentSearches].slice(0, 3);
        setRecentSearches(newRecentSearches);
      }
      const filterType = selectedFilter.length === filters.length ? null : selectedFilter.map((filter) => filter.value).join(',');
      onSearch(trimmedQuery, filterType);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleFilterChange = (filter: FilterType) => {
    setSelectedFilter([filter]);
    inputRef.current?.focus();
  };

  const handleResetFilters = () => {
    setSelectedFilter(filters);
    inputRef.current?.focus();
  };

  const handleRecentSearchClick = (search: RecentSearch) => {
    setSearchQuery(search.text);
    setSelectedFilter(search.filters);
    const filterType = search.filters.length === filters.length ? null : search.filters.map((filter) => filter.value).join(',');
    onSearch(search.text, filterType);
  };

  const expandedPanelRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(expandedPanelRef, () => setIsExpanded(false));

  const getColorBg = () => {
    if (userAuth?.admin) {
      return 'bg-[#289548]';
    }
    return 'bg-[#131324]';
  }

  const getColorBgButton = () => {
    if (userAuth?.admin) {
      return 'bg-[#19652F]';
    }
    return 'bg-[#165696]';
  }

  return (
    <Box className="relative w-[650px]">
      <Box className={`rounded-lg h-12 ${getColorBg()}`}>
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white" />
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyUp={handleKeyPress}
          onFocus={() => setIsExpanded(true)}
          placeholder="Buscar"
          className="w-full pl-10 ml-1 pr-4 py-3 bg-transparent text-white placeholder-white rounded-lg outline-none focus:outline-none transition-all"
          style={{ boxShadow: 'none', border: 'none' }}
        />
      </Box>

      {isExpanded && (
        <Box
          // @ts-ignore
          ref={expandedPanelRef}
          className={`absolute left-0 top-full w-full z-10 rounded-b-lg shadow-lg border border-[#35354a] border-t-1 border-t-[#ffffff] ${getColorBg()}`}
        >
          <Box className="flex gap-2 px-4 pt-3 pb-2">
            {selectedFilter.map((filter) => (
              <div
                key={filter.value}
                className={`flex items-center text-white rounded-full px-4 py-2 mr-2 cursor-pointer ${getColorBgButton()}`}
                onClick={() => handleFilterChange(filter)}
              >
                <span className="text-md">{filter.label}</span>
                {selectedFilter.length === 1 &&
                  <CloseIcon className="text-white ml-2 hover:cursor-pointer" style={{ fontSize: 22 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleResetFilters();
                    }} />
                }
              </div>
            ))}
          </Box>
          <div className="border-b border-[#ffffff] w-full" />

          {recentSearches.length > 0 && (
            <Box className="px-4 py-3">
              <Box className="mb-2">
                <h3 className="text-gray-400 font-medium text-sm">Pesquisas recentes</h3>
              </Box>
              <Box className="space-y-2">
                {recentSearches.map((search, index) => (
                  <Box
                    key={index}
                    className="flex items-center gap-2 p-0 bg-transparent rounded-lg cursor-pointer group"
                    onClick={() => handleRecentSearchClick(search)}
                  >
                    <span
                      className={`flex items-center justify-center w-8 h-8 p-2 rounded-full ${getColorBgButton()}`}
                    >
                      <AccessTime className="text-white text-lg" />
                    </span>
                    <span className="text-white text-sm font-medium truncate">{search.text}</span>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VehicleSearchBar;